import type { FetchResult } from '@apollo/client'
import { ApolloClient, createHttpLink, fromPromise } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import * as Sentry from '@sentry/browser'
import type Observable from 'zen-observable'

import { REGENERATE_TOKEN } from '../data/mutations/auth'
import { tokenUtils } from '../utils/token-utils'
import { cache } from './apollo-cache'
import { envConfig } from './env-config'

// Types
interface TokenResponse {
  regenerateToken: {
    accessToken: string
  }
}

interface GraphQLErrorExtensions {
  response?: {
    body: {
      message: string
    }
  }
}

interface CustomGraphQLError extends Error {
  extensions?: GraphQLErrorExtensions
}

// State management for token refresh
let isRefreshing = false
let pendingRequests: Array<() => void> = []

const setIsRefreshing = (value: boolean): void => {
  isRefreshing = value
}

const resetPendingRequests = (): void => {
  pendingRequests = []
}

const addPendingRequest = (callback: () => void): void => {
  pendingRequests.push(callback)
}

const resolvePendingRequests = (): void => {
  pendingRequests.forEach((callback) => callback())
  resetPendingRequests()
}

// Create a separate client for token renewal
const renewTokenApiClient = new ApolloClient({
  cache,
  credentials: 'include',
  link: createHttpLink({ uri: envConfig.graphqlUrl })
})

// Token management functions
const getNewToken = async (): Promise<string> => {
  const refreshToken = tokenUtils.getToken('Refresh-Token') || ''
  try {
    const { data } = await renewTokenApiClient.mutate<TokenResponse>({
      mutation: REGENERATE_TOKEN,
      variables: { refreshToken }
    })

    if (!data?.regenerateToken.accessToken) {
      throw new Error('No access token received')
    }

    return data.regenerateToken.accessToken
  } catch (error) {
    console.error('Error refreshing token:', error)
    throw error
  }
}

const logOut = (): void => {
  if (typeof window !== 'undefined') {
    Sentry.captureMessage('Auto logout triggered due to authentication error', {
      level: 'warning',
      tags: {
        action: 'auto-logout',
        source: 'error-link'
      }
    })
    tokenUtils.clearLocalStore()
    window.location.replace(`${window.location.origin}/login`)
  }
}

// Error link implementation
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }): Observable<FetchResult> | void => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const error = err as CustomGraphQLError

        if (error.extensions?.response?.body) {
          const { message } = error.extensions.response.body

          switch (message) {
            case 'Token Expired': {
              let forward$

              if (!isRefreshing) {
                setIsRefreshing(true)

                forward$ = fromPromise(
                  (async () => {
                    try {
                      const token = await getNewToken()
                      tokenUtils.setToken('Access-Token', token)
                      resolvePendingRequests()
                      return token
                    } catch (error) {
                      const refreshToken = tokenUtils.getToken('Refresh-Token')
                      const accessToken = tokenUtils.getToken('Access-Token')
                      Sentry.captureException(error, {
                        extra: {
                          accessToken: accessToken ? 'present' : 'missing',
                          refreshToken: refreshToken ? 'present' : 'missing'
                        },
                        tags: {
                          action: 'token-refresh-failed',
                          source: 'error-link'
                        }
                      })
                      resetPendingRequests()
                      logOut()
                      return null
                    } finally {
                      setIsRefreshing(false)
                    }
                  })()
                ).filter((value) => Boolean(value))
              } else {
                forward$ = fromPromise(
                  new Promise<void>((resolve) => {
                    addPendingRequest(() => resolve())
                  })
                )
              }

              return forward$.flatMap(() => forward(operation))
            }

            case 'Invalid Token': {
              const accessToken = tokenUtils.getToken('Access-Token')
              const refreshToken = tokenUtils.getToken('Refresh-Token')
              Sentry.captureMessage('Invalid token detected, triggering auto logout', {
                extra: {
                  accessToken: accessToken ? 'present' : 'missing',
                  refreshToken: refreshToken ? 'present' : 'missing'
                },
                level: 'warning',
                tags: {
                  action: 'invalid-token',
                  source: 'error-link'
                }
              })
              logOut()
              break
            }

            default: {
              // Handle other GraphQL errors if needed
              console.error(`[GraphQL error]: ${message}`)
            }
          }
        }
      }
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`)
    }
  }
)

export default errorLink
